import type {BadgeProps} from "@/components/ui/badge";
import moment from "moment-timezone";

import type {Safelisting} from "../types";
import {
  getSafelistingDisplayBadgeProps,
} from "./safelisting-parent-status";

/** Overall permit lifecycle from first `start_datetime` through last `end_datetime`. */
export type SafelistingCoverageStatus = "scheduled" | "active" | "expired" | "cancelled";
type SafelistingDay = Safelisting["days"][number];

function nowInZone(timeZone?: string): moment.Moment {
  return timeZone ? moment.tz(timeZone) : moment();
}

function dayCoverageEnd(day: SafelistingDay, timeZone?: string): moment.Moment {
  if (day.end_datetime?.trim()) {
    return timeZone ? moment.tz(day.end_datetime, timeZone) : moment(day.end_datetime);
  }
  const anchor = timeZone
    ? moment.tz(day.date, "YYYY-MM-DD", timeZone)
    : moment(day.date, "YYYY-MM-DD");
  return anchor.endOf("day");
}

function activeDays(safelisting: Safelisting): SafelistingDay[] {
  return (safelisting.days ?? []).filter((d) => d.status !== "cancelled");
}

function isCancelledSafelisting(safelisting: Safelisting): boolean {
  if (safelisting.status === 0 || safelisting.status === "0") return true;
  if (typeof safelisting.status === "string" && safelisting.status.toLowerCase() === "cancelled") {
    return true;
  }
  const days = safelisting.days ?? [];
  return days.length > 0 && days.every((d) => d.status === "cancelled");
}

/** Latest coverage end across all permit days (overall permit end). */
function permitCoverageEnd(safelisting: Safelisting, timeZone?: string): moment.Moment | null {
  const days = activeDays(safelisting);
  if (days.length === 0) return null;

  let latest: moment.Moment | null = null;
  for (const day of days) {
    const end = dayCoverageEnd(day, timeZone);
    if (latest == null || end.isAfter(latest)) {
      latest = end;
    }
  }
  return latest;
}

/** Earliest coverage start across all permit days (overall permit start). */
function permitCoverageStart(safelisting: Safelisting, timeZone?: string): moment.Moment | null {
  const days = activeDays(safelisting);
  if (days.length === 0) return null;

  let earliest: moment.Moment | null = null;
  for (const day of days) {
    const start = day.start_datetime?.trim()
      ? timeZone
        ? moment.tz(day.start_datetime, timeZone)
        : moment(day.start_datetime)
      : timeZone
        ? moment.tz(day.date, "YYYY-MM-DD", timeZone).startOf("day")
        : moment(day.date, "YYYY-MM-DD").startOf("day");
    if (earliest == null || start.isBefore(earliest)) {
      earliest = start;
    }
  }
  return earliest;
}

export function getSafelistingCoverageStatus(
  safelisting: Safelisting,
  timeZone?: string,
): SafelistingCoverageStatus {
  if (isCancelledSafelisting(safelisting)) return "cancelled";
  const now = nowInZone(timeZone);
  const start = permitCoverageStart(safelisting, timeZone);
  const end = permitCoverageEnd(safelisting, timeZone);

  if (!end || !end.isAfter(now)) return "expired";
  if (start && now.isBefore(start)) return "scheduled";
  return "active";
}

export function getSafelistingCoverageStatusBadgeProps(
  status: SafelistingCoverageStatus,
): {label: string; variant: BadgeProps["variant"]} {
  switch (status) {
    case "scheduled":
      return {label: "Scheduled", variant: "info"};
    case "active":
      return {label: "Active", variant: "success"};
    case "expired":
      return {label: "Expired", variant: "warning"};
    case "cancelled":
      return {label: "Cancelled", variant: "error"};
  }
}

/** Display badge — parent suspended or household amenity overlay on active/scheduled coverage. */
export function getSafelistingCoverageStatusBadgePropsForDisplay(
  status: SafelistingCoverageStatus,
  amenityRestricted: boolean,
  source?: Pick<Safelisting, "status" | "status_label" | "amenity_access_restricted" | "household">,
): {label: string; variant: BadgeProps["variant"]} {
  if (source) {
    return getSafelistingDisplayBadgeProps(
      source,
      status,
      amenityRestricted,
    );
  }
  if (amenityRestricted && (status === "active" || status === "scheduled")) {
    return {label: "Suspended", variant: "warning"};
  }
  return getSafelistingCoverageStatusBadgeProps(status);
}

/**
 * Whether the permit is not fully expired (scheduled + in-progress).
 * Used for edit/delete and “non-past” list ordering — not the status badge label.
 */
export function hasFutureDate(safelisting: Safelisting, timeZone?: string): boolean {
  const status = getSafelistingCoverageStatus(safelisting, timeZone);
  return status === "scheduled" || status === "active";
}
/** True when at least one non-cancelled day’s coverage has fully ended but the permit has not. */
export function hasPastDate(safelisting: Safelisting, timeZone?: string): boolean {
  const days = activeDays(safelisting);
  if (days.length === 0) return false;
  const now = nowInZone(timeZone);
  const permitEnded = !hasFutureDate(safelisting, timeZone);
  if (permitEnded) return true;

  const permitStart = permitCoverageStart(safelisting, timeZone);
  if (permitStart && now.isBefore(permitStart)) return false;

  return days.some((day) => dayCoverageEnd(day, timeZone).isBefore(now));
}
